class ReasonsToBuyCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.js-reasons-viewport')
    this.track = this.querySelector('.js-reasons-track')
    this.slides = Array.from(this.querySelectorAll('.js-reasons-slide'))
    this.prevButton = this.querySelector('.js-reasons-prev')
    this.nextButton = this.querySelector('.js-reasons-next')

    this.desktopStep = Number(this.dataset.desktopStep || 2)
    this.desktopVisible = Number(this.dataset.desktopVisible || 2.5)
    this.desktopGap = Number(this.dataset.desktopGap || 24)
    this.desktopQuery = window.matchMedia('(min-width: 750px)')

    this.pagePositions = [0]
    this.currentPageIndex = 0
    this._rafId = 0
    this.touchStartX = 0
    this.touchStartY = 0
    this.touchStartIndex = 0
    this.touchThreshold = 30

    this.onScroll = this.onScroll.bind(this)
    this.onResize = this.onResize.bind(this)
    this.onPrev = this.onPrev.bind(this)
    this.onNext = this.onNext.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    this.onTouchStart = this.onTouchStart.bind(this)
    this.onTouchEnd = this.onTouchEnd.bind(this)
    this.onTouchCancel = this.onTouchCancel.bind(this)
    this.refresh = this.refresh.bind(this)

    this.resizeObserver = new ResizeObserver(this.refresh)

    this.viewport?.addEventListener('scroll', this.onScroll, { passive: true })
    this.viewport?.addEventListener('keydown', this.onKeydown)
    this.viewport?.addEventListener('touchstart', this.onTouchStart, {
      passive: true,
    })
    this.viewport?.addEventListener('touchend', this.onTouchEnd, {
      passive: true,
    })
    this.viewport?.addEventListener('touchcancel', this.onTouchCancel, {
      passive: true,
    })
    this.prevButton?.addEventListener('click', this.onPrev)
    this.nextButton?.addEventListener('click', this.onNext)
    this.resizeObserver.observe(this.viewport)
    window.addEventListener('resize', this.onResize)

    this.refresh()
    this._rafId = requestAnimationFrame(this.refresh)
  }

  disconnectedCallback() {
    this.viewport?.removeEventListener('scroll', this.onScroll)
    this.viewport?.removeEventListener('keydown', this.onKeydown)
    this.viewport?.removeEventListener('touchstart', this.onTouchStart)
    this.viewport?.removeEventListener('touchend', this.onTouchEnd)
    this.viewport?.removeEventListener('touchcancel', this.onTouchCancel)
    this.prevButton?.removeEventListener('click', this.onPrev)
    this.nextButton?.removeEventListener('click', this.onNext)
    this.resizeObserver?.disconnect()
    window.removeEventListener('resize', this.onResize)

    if (this._rafId) {
      cancelAnimationFrame(this._rafId)
    }
  }

  onResize() {
    this.refresh()
  }

  onScroll() {
    this.updateControls()
  }

  onKeydown(event) {
    if (!this.desktopQuery.matches) return

    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      this.onPrev(event)
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      this.onNext(event)
    }
  }

  onTouchStart(event) {
    if (this.desktopQuery.matches || !event.touches.length) return

    const touch = event.touches[0]
    this.touchStartX = touch.clientX
    this.touchStartY = touch.clientY
    this.touchStartIndex = this.currentPageIndex
  }

  onTouchEnd(event) {
    if (this.desktopQuery.matches || !event.changedTouches.length) return

    const touch = event.changedTouches[0]
    const deltaX = touch.clientX - this.touchStartX
    const deltaY = touch.clientY - this.touchStartY

    if (Math.abs(deltaX) < this.touchThreshold) return
    if (Math.abs(deltaX) <= Math.abs(deltaY)) return

    if (deltaX < 0) {
      if (this.touchStartIndex >= this.pagePositions.length - 1) {
        this.scrollToPage(0)
        return
      }

      this.scrollToPage(this.touchStartIndex + 1)
      return
    }

    this.scrollToPage(Math.max(this.touchStartIndex - 1, 0))
  }

  onTouchCancel() {
    this.touchStartIndex = this.currentPageIndex
  }

  refresh() {
    if (!this.viewport || !this.slides.length) return

    const isDesktop = this.desktopQuery.matches
    const gap = isDesktop ? this.desktopGap : 5
    const visibleSlides = isDesktop ? this.desktopVisible : 1
    const viewportWidth = this.viewport.clientWidth

    if (viewportWidth <= 1) {
      return
    }
    const slideWidth = Math.max(
      1,
      (viewportWidth - gap * (visibleSlides - 1)) / visibleSlides
    )

    this.viewport.style.setProperty('--reasons-track-gap', `${gap}px`)
    this.viewport.style.setProperty('--reasons-slide-width', `${slideWidth}px`)

    this.pagePositions = this.buildPositions(isDesktop)
    this.updateControls()
  }

  buildPositions(isDesktop) {
    const totalSlides = this.slides.length
    const maxScroll = Math.max(
      0,
      this.viewport.scrollWidth - this.viewport.clientWidth
    )

    if (totalSlides <= 1 || maxScroll <= 0) {
      return [0]
    }

    if (!isDesktop) {
      return this.slides.map((slide) => slide.offsetLeft)
    }

    const positions = [0]
    let index = 0

    while (index < totalSlides - 1) {
      const candidate = index + this.desktopStep

      if (candidate >= totalSlides) {
        break
      }

      const remainingSlides = totalSlides - candidate

      if (remainingSlides <= this.desktopVisible) {
        break
      }

      const nextPosition = this.slides[candidate].offsetLeft

      if (positions[positions.length - 1] !== nextPosition) {
        positions.push(nextPosition)
      }

      index = candidate
    }

    if (positions[positions.length - 1] !== maxScroll) {
      positions.push(maxScroll)
    }

    return positions
  }

  getCurrentPageIndex() {
    if (!this.pagePositions.length) return 0

    const currentScrollLeft = this.viewport?.scrollLeft || 0
    let closestIndex = 0
    let closestDistance = Number.POSITIVE_INFINITY

    this.pagePositions.forEach((position, index) => {
      const distance = Math.abs(position - currentScrollLeft)

      if (distance < closestDistance) {
        closestDistance = distance
        closestIndex = index
      }
    })

    return closestIndex
  }

  scrollToPage(index, behavior = 'smooth') {
    if (!this.pagePositions.length) return

    const clampedIndex = Math.max(
      0,
      Math.min(index, this.pagePositions.length - 1)
    )

    this.currentPageIndex = clampedIndex

    this.viewport.scrollTo({
      left: this.pagePositions[clampedIndex] || 0,
      behavior,
    })

    this.updateControls(clampedIndex)
  }

  onPrev(event) {
    event?.preventDefault?.()
    if (!this.pagePositions.length) return

    const currentIndex = this.desktopQuery.matches
      ? this.getCurrentPageIndex()
      : this.currentPageIndex
    this.scrollToPage(Math.max(currentIndex - 1, 0))
  }

  onNext(event) {
    event?.preventDefault?.()
    if (!this.pagePositions.length) return

    const currentIndex = this.desktopQuery.matches
      ? this.getCurrentPageIndex()
      : this.currentPageIndex

    if (currentIndex >= this.pagePositions.length - 1) {
      this.scrollToPage(0)
      return
    }

    this.scrollToPage(currentIndex + 1)
  }

  updateControls(forcedIndex) {
    const isDesktop = this.desktopQuery.matches
    const hasOverflow = this.pagePositions.length > 1
    const currentScrollLeft = this.viewport?.scrollLeft || 0
    const currentIndex =
      typeof forcedIndex === 'number' ? forcedIndex : this.getCurrentPageIndex()
    const atStart = currentScrollLeft <= 2
    const atEnd =
      hasOverflow &&
      (typeof forcedIndex === 'number'
        ? forcedIndex >= this.pagePositions.length - 1
        : currentIndex >= this.pagePositions.length - 1 &&
          Math.abs(
            (this.viewport?.scrollLeft || 0) -
              (this.pagePositions[this.pagePositions.length - 1] || 0)
          ) < 2)

    this.currentPageIndex = currentIndex

    if (this.prevButton) {
      this.prevButton.classList.toggle('is-hidden', !hasOverflow || atStart)
      this.prevButton.setAttribute(
        'aria-hidden',
        !hasOverflow || atStart ? 'true' : 'false'
      )
    }

    if (this.nextButton) {
      this.nextButton.classList.toggle('is-hidden', !hasOverflow)
      this.nextButton.setAttribute(
        'aria-hidden',
        hasOverflow ? 'false' : 'true'
      )
      this.nextButton.setAttribute(
        'aria-label',
        atEnd ? 'Return to beginning' : 'Next graphics'
      )
      this.nextButton.dataset.mode = atEnd ? 'rewind' : 'advance'
    }
  }
}

customElements.define('hh-reasons-to-buy-carousel', ReasonsToBuyCarousel)
